/**
 * Staged un-noindex helper. Mirrors backend SEO_INDEX_STAGE so per-page
 * `noindex` meta and the sitemap-emitted slug list agree at deploy time.
 *
 * Set `VITE_SEO_INDEX_STAGE=0|1|2|3|all` at build. Default = "0" (all
 * country pages noindex).
 *
 *   0   — all country pages noindex (we're still in QA / pre-Deploy B)
 *   1   — top-20 tourist indexable, rest noindex (May 7)
 *   2   — top-70 indexable (May 14)
 *   3   — all 192 indexable (May 21)
 *   all — alias for 3
 */
// Same JSON the rest of the frontend reads — single source of truth.
import countriesData from '../../data/prices.json';

interface StagedCountry { slug: string; }
interface PricesShape {
  countries?: StagedCountry[];
  topTourismSorted?: string[];
}

const STAGE = (import.meta.env?.VITE_SEO_INDEX_STAGE ?? '0').toLowerCase();
// Stages 3 and all index every country (stagedSlugs); the limit only has to be non-zero.
const STAGE_LIMIT: Record<string, number> = { '0': 0, '1': 20, '2': 70, '3': Infinity, 'all': Infinity };
const COUNTRY_LIMIT = STAGE_LIMIT[STAGE] ?? 0;

let _staged: Set<string> | null = null;
function stagedSlugs(): Set<string> {
  if (_staged) return _staged;
  if (COUNTRY_LIMIT === 0) {
    _staged = new Set();
    return _staged;
  }
  const data = countriesData as PricesShape;
  const all = (data.countries ?? []).map((c) => c.slug);
  if (STAGE === 'all' || STAGE === '3') {
    _staged = new Set(all);
    return _staged;
  }
  // topTourismSorted is 50 entries; for Stage 2 (limit=70) we top up with
  // remaining priced slugs in alpha order so the frontend `noindex` predicate
  // matches the backend sitemap exactly. (Same fix as backend/src/routes/seo.ts.)
  const top = (data.topTourismSorted && data.topTourismSorted.length > 0)
    ? data.topTourismSorted
    : [...all].sort();
  const seen = new Set(top);
  const remaining = all.filter((s) => !seen.has(s)).sort();
  const order = [...top, ...remaining];
  _staged = new Set(order.slice(0, COUNTRY_LIMIT));
  return _staged;
}

/** True iff `slug` is currently indexable (lives in the active stage cohort). */
export function isCountryIndexable(slug: string): boolean {
  return stagedSlugs().has(slug);
}
